import React from 'react';
import { Box, FormControl, Grid, MenuItem, Paper, Select, TextField } from '@mui/material';

import AppForm from '@/__core/components/AppForm';
import { LabeledField } from '@/__core/components/LabeledField';
import { sharedFormStyles } from '@/__core/styles/sharedFormStyles';
import { getStatusOptionsByModule } from '@/__core/utils/statusByModuleCache';
import AddressFieldWrapper from '@/modules/addresses/components/AddressFieldWrapper';

import { createEmptyStoreAddress, hasMeaningfulStoreFormInput, InventoryStoreFormData } from '../models/StoreModel';

type StoreStatusOption = {
  id: string;
  name: string;
  color?: string;
  is_default?: boolean;
  sort_order?: number;
  group?: {
    id?: string;
    name?: string;
    sort_order?: number;
  };
};

interface StoreFormProps {
  formData: InventoryStoreFormData;
  canEdit?: boolean;
  statusOptions: StoreStatusOption[];
  onChangeForm: (updates: Partial<InventoryStoreFormData>) => void;
  onSave: () => void;
  onCancel: () => void;
  saving?: boolean;
  submitDisabled?: boolean;
  isUpdateMode?: boolean;
}

export default function StoreForm({
  formData,
  canEdit = true,
  statusOptions,
  onChangeForm,
  onSave,
  onCancel,
  saving = false,
  submitDisabled,
  isUpdateMode = false,
}: StoreFormProps) {
  const readOnly = !canEdit;
  const [apiStatusOptions, setApiStatusOptions] = React.useState<StoreStatusOption[]>([]);

  React.useEffect(() => {
    let isCancelled = false;

    getStatusOptionsByModule(['store'])
      .then((options) => {
        if (!isCancelled) {
          setApiStatusOptions(options);
        }
      })
      .catch(() => {
        if (!isCancelled) {
          setApiStatusOptions([]);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, []);

  const resolvedStatusOptions = React.useMemo(() => {
    const sourceOptions = apiStatusOptions.length > 0 ? apiStatusOptions : statusOptions;

    return sourceOptions
      .filter((option) => option.id && option.id !== 'all')
      .sort((a, b) => {
        const groupSortA = a.group?.sort_order ?? 999;
        const groupSortB = b.group?.sort_order ?? 999;
        if (groupSortA !== groupSortB) return groupSortA - groupSortB;

        const itemSortA = a.sort_order ?? 0;
        const itemSortB = b.sort_order ?? 0;
        if (itemSortA !== itemSortB) return itemSortA - itemSortB;

        return (a.name || '').localeCompare(b.name || '', 'vi');
      });
  }, [apiStatusOptions, statusOptions]);

  React.useEffect(() => {
    if (apiStatusOptions.length === 0) {
      return;
    }

    const defaultStatus = resolvedStatusOptions.find((option) => option.is_default) || resolvedStatusOptions[0];
    if (!defaultStatus) {
      return;
    }

    const matchedStatus = resolvedStatusOptions.some((option) => option.id === formData.status);
    if (!formData.status || (!matchedStatus && formData.status === 'active')) {
      onChangeForm({ status: defaultStatus.id });
    }
  }, [apiStatusOptions.length, formData.status, onChangeForm, resolvedStatusOptions]);

  const selectedStatus = resolvedStatusOptions.find((option) => option.id === formData.status);
  const hasMeaningfulInput = React.useMemo(() => hasMeaningfulStoreFormInput(formData), [formData]);

  return (
    <AppForm
      onSubmit={onSave}
      onCancel={onCancel}
      submitting={saving}
      submitDisabled={submitDisabled ?? (saving || readOnly || !hasMeaningfulInput)}
      submitLabel='Lưu & Đóng'
      submittingLabel='Đang lưu...'
      containerSx={{
        m: 0,
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        overflowX: 'hidden',
      }}
    >
      <Paper sx={{ ...sharedFormStyles.formCard, mb: 2, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>

        <Grid container rowSpacing={1} columnSpacing={5}>
          <Grid size={{ xs: 12, md: 6 }}>
            
            <LabeledField label='Tên kho' labelWidth={100}>
              <TextField
                size='small'
                value={formData.name}
                onChange={(event) => onChangeForm({ name: event.target.value })}
                disabled={readOnly}
                fullWidth
                autoComplete='off'
              />
            </LabeledField>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <LabeledField label='Trạng thái' labelWidth={100}>
              <FormControl fullWidth size='small'>
                <Select
                  value={formData.status}
                  onChange={(event) => onChangeForm({ status: event.target.value })}
                  disabled={readOnly}
                  sx={sharedFormStyles.inputArrowAlign}
                  renderValue={(selected) => {
                    const selectedOption = resolvedStatusOptions.find((option) => option.id === selected) || selectedStatus;

                    if (!selectedOption) {
                      return selected || '';
                    }

                    return (
                      <Box component='span' sx={{ color: selectedOption.color || 'inherit', fontWeight: 500 }}>
                        {selectedOption.name}
                      </Box>
                    );
                  }}
                >
                  {resolvedStatusOptions.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      <Box component='span' sx={{ color: option.color || 'inherit', fontWeight: 500 }}>
                        {option.name}
                        {option.is_default ? ' (Mặc định)' : ''}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </LabeledField>
          </Grid>
        </Grid>

        <AddressFieldWrapper
          initialAddress={formData.address || createEmptyStoreAddress()}
          onAddressChange={(address) => onChangeForm({ address })}
          isEditMode={!readOnly}
          labelWidth={100}
          styleDisplay='small'
          disabled={readOnly}
          defaultProvinceOnEmpty={!isUpdateMode}
        />
      </Paper>
    </AppForm>
  );
}
